// eslint-disable-next-line jira/restricted/react
import React, { PureComponent } from 'react';
import type { Store } from 'redux';
import AppBase from '@atlassian/jira-app-base';
import type { ProjectType } from '@atlassian/jira-common-constants/src/project-types';
import { type CloudName, type AccessLevel, toCloudName } from '@atlassian/jira-shared-types';
import { TenantContextSubscriber } from '@atlassian/jira-tenant-context-controller';
import { setProjectAccessType, setContext } from '../../action-creators';
import type { State, AccessType } from '../../types';
import ProjectAccessFieldView from '../field/project-access-field-view';
import type { DropdownWidthStyles } from '../field/types';
import { initialState } from '../project-access-root-reducer';
import createStore from './create-store';

type InnerProps = {
	value?: AccessLevel;
	onFirstChange: (selection: AccessLevel) => void;
	onAccessTypesLoaded: () => void;
	onChange: (selection: AccessLevel) => void;
	onError: () => void;
	baseUrl?: string;
	projectId?: number;
	projectType?: ProjectType;
	isDisabled: boolean;
	cloudName: CloudName;
	isSiteAdmin: boolean;
	validateRequired?: boolean;
	requiredAccess?: boolean;
	dropdownWidthStyles?: DropdownWidthStyles;
};

type TenantContextProps = {
	cloudName: CloudName;
	isSiteAdmin: boolean;
};

export type Props = Flow.Diff<InnerProps, TenantContextProps>;

const getValueOnlyAccessType = (value: AccessLevel): AccessType => ({
	value,
	// there are non-critical information that are used only for displaying dropdown options
	description: '',
	iconName: '',
	displayName: '',
});

// eslint-disable-next-line jira/react/no-class-components
class ProjectAccessApp extends PureComponent<InnerProps> {
	// Kinda hacky: In order to get a feature flag check around the TenantContextSubscriber,
	// we need to pass 'dummy' prop values for these.
	static defaultProps = {
		isSiteAdmin: false,
		cloudName: toCloudName('DISABLED'),
		validateRequired: false,
		requiredAccess: false,
	};

	constructor(props: InnerProps) {
		super(props);
		const {
			baseUrl = '',
			cloudName,
			isSiteAdmin,
			value,
			projectId,
			projectType,
			isDisabled,
		} = this.props;

		this.store = createStore(initialState);
		this.store.dispatch(
			setContext({ baseUrl, cloudName, isSiteAdmin, projectId, projectType, isDisabled }),
		);

		if (value) {
			this.store.dispatch(setProjectAccessType(getValueOnlyAccessType(value)));
		}
	}

	UNSAFE_componentWillReceiveProps(nextProps: InnerProps) {
		if (nextProps.value && nextProps.value !== this.props.value) {
			this.store.dispatch(setProjectAccessType(getValueOnlyAccessType(nextProps.value)));
		}
	}

	store: Store<State>;

	render = () => (
		<AppBase store={this.store} teamName="helix" packageName="projectAccess">
			<ProjectAccessFieldView
				onFirstChange={this.props.onFirstChange}
				onAccessTypesLoaded={this.props.onAccessTypesLoaded}
				onChange={this.props.onChange}
				onError={this.props.onError}
				isDisabled={this.props.isDisabled}
				validateRequired={this.props.validateRequired}
				requiredAccess={this.props.requiredAccess}
				dropdownWidthStyles={this.props.dropdownWidthStyles}
			/>
		</AppBase>
	);
}

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (props: Props) => (
	<TenantContextSubscriber>
		{({ tenantContext }) => (
			<ProjectAccessApp
				cloudName={tenantContext.cloudName}
				isSiteAdmin={tenantContext.isSiteAdmin}
				{...props}
			/>
		)}
	</TenantContextSubscriber>
);
